import { useToasts } from "@certn/cascadia";
import {
  BasicButton,
  Link,
  PrimaryButton,
  SectionMessage,
  Text,
  useColors,
} from "@certn/cascadia-react";
import { css } from "@emotion/css";
import { useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import Webcam from "react-webcam";

import { usePhotoUpload } from "./hooks";
import { usePhotoVerificationStyles } from "./styles";

export function SelfieUpload({
  onUpload,
}: {
  onUpload: (documentId: string) => void;
}) {
  const { t } = useTranslation();
  const styles = usePhotoVerificationStyles();
  const webcamRef = useRef<Webcam>(null);
  const [userMedia, setUserMedia] = useState<MediaStream>();
  const [userMediaError, setUserMediaError] = useState<string | DOMException>();
  const [imgDataUri, setImgDataUri] = useState<string | null>();
  const [isUploaded, setIsUploaded] = useState<boolean>();
  const [isLoading, setIsLoading] = useState<boolean>();
  const colors = useColors();
  const { show } = useToasts();
  const upload = usePhotoUpload();

  const handleUpload = async () => {
    if (!imgDataUri) {
      return;
    }

    setIsUploaded(false);
    setIsLoading(true);

    try {
      const documentId = await upload(imgDataUri);
      setIsUploaded(true);
      onUpload(documentId);
    } catch {
      show({
        title: t("Something went wrong, try again"),
        type: "error",
      });
    }

    setIsLoading(false);
  };

  return (
    <>
      {!imgDataUri && userMedia && (
        <div
          className={css(styles.ellipse, {
            border: `4px solid ${colors["border-inverse"]}`,
          })}
        />
      )}

      <Webcam
        data-testid="webcam"
        audio={false}
        ref={webcamRef}
        screenshotFormat="image/jpeg"
        className={css({
          display: imgDataUri ? "none" : "block",
          width: "100%",
        })}
        videoConstraints={{ aspectRatio: 1 }}
        onUserMediaError={setUserMediaError}
        onUserMedia={setUserMedia}
      />

      {imgDataUri && (
        <img alt="selfie" className={styles.image} src={imgDataUri} />
      )}

      {userMediaError ? (
        <div className={styles.errorContainer}>
          <Text style="form/body-primary" color="text-weak">
            <Trans>
              Camera permission is not enabled and a camera is needed for
              identity verification.
            </Trans>
            <br />
            <br />
            <Link
              testID="photo-verification-enable-camera-link"
              href="https://help.certn.co/hc/en-us/articles/9940180160659-Enable-your-camera"
              linkStyle="monochrome"
              textColor="text-weak"
            >
              <Trans>How to enable the camera</Trans>
            </Link>
          </Text>
        </div>
      ) : (
        <div className={styles.footer}>
          {isUploaded && (
            <div style={{ width: "100%" }}>
              <SectionMessage
                sectionTitle={t("Upload successful")}
                type="success"
                icon="check"
              >
                <Trans>Please continue to the next step</Trans>
              </SectionMessage>
            </div>
          )}

          {!isUploaded &&
            (imgDataUri ? (
              <>
                <BasicButton
                  size="small"
                  leftIcon="photo_camera"
                  onPress={() => setImgDataUri(null)}
                  isLoading={isLoading}
                  isDisabled={isLoading}
                  testID="photo-verification-retake-photo-button"
                >
                  <Trans>Retake photo</Trans>
                </BasicButton>
                <PrimaryButton
                  size="small"
                  leftIcon="check_circle"
                  onPress={handleUpload}
                  isLoading={isLoading}
                  isDisabled={isLoading}
                  testID="photo-verification-confirm-photo-button"
                >
                  <Trans>Confirm photo</Trans>
                </PrimaryButton>
              </>
            ) : (
              <BasicButton
                size="small"
                leftIcon="photo_camera"
                onPress={() =>
                  setImgDataUri(webcamRef.current?.getScreenshot())
                }
                testID="photo-verification-capture-photo-button"
              >
                <Trans>Capture photo</Trans>
              </BasicButton>
            ))}
        </div>
      )}
    </>
  );
}
